import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { formatNumber } from '../../utils/helpers';
import './PolymarketStats.css';

// Format dollar amount for display
const formatDollarAmount = (value) => {
  if (value >= 1000000) {
    return `$${(value / 1000000).toFixed(2)}M`;
  } else if (value >= 1000) {
    return `$${(value / 1000).toFixed(2)}K`;
  }
  return `$${value.toFixed(2)}`;
};

function PolymarketStats({ summary, loading, trades = [] }) {
  const { t } = useTranslation();

  // Find the largest trade for Market Info panel
  const largestTrade = useMemo(() => {
    if (!trades || trades.length === 0) return null;
    return trades.reduce((max, trade) =>
      (trade.dollarSize > (max?.dollarSize || 0)) ? trade : max
    , null);
  }, [trades]);

  if (loading || !summary) {
    return (
      <div className="polymarket-stats-container">
        <div className="polymarket-stat-card">
          <span className="polymarket-stat-label">{t('polymarket.whaleVolume24h')}</span>
          <span className="polymarket-stat-value polymarket-loading">-</span>
        </div>
        <div className="polymarket-stat-card">
          <span className="polymarket-stat-label">{t('polymarket.totalTransactions')}</span>
          <span className="polymarket-stat-value polymarket-loading">-</span>
        </div>
        <div className="polymarket-largest-block empty">
          <div className="polymarket-largest-left">
            <span className="polymarket-stat-label">{t('polymarket.largestTrade')}</span>
            <span className="polymarket-largest-value polymarket-loading">-</span>
          </div>
          <div className="polymarket-largest-divider"></div>
          <div className="polymarket-largest-right">
            <span className="polymarket-largest-market-label">{t('polymarket.marketInfo')}</span>
            <span className="polymarket-largest-empty">-</span>
          </div>
        </div>
      </div>
    );
  }

  // Determine if YES or NO for largest trade (BUY = YES, SELL = NO)
  const isYes = largestTrade?.side === 'BUY';
  const displaySide = isYes ? t('polymarket.yes').toUpperCase() : t('polymarket.no').toUpperCase();

  return (
    <div className="polymarket-stats-container">
      <div className="polymarket-stat-card">
        <span className="polymarket-stat-label">{t('polymarket.whaleVolume24h')}</span>
        <span className="polymarket-stat-value polymarket-volume">${formatNumber(summary.totalVolume || 0)}</span>
      </div>
      <div className="polymarket-stat-card">
        <span className="polymarket-stat-label">{t('polymarket.totalTransactions')}</span>
        <span className="polymarket-stat-value polymarket-count">{summary.tradeCount || 0}</span>
      </div>

      {/* Largest Trade + Market Info - Combined Block */}
      {largestTrade ? (
        <a
          className="polymarket-largest-block"
          href={`https://polymarket.com/event/${largestTrade.eventSlug || largestTrade.marketSlug}`}
          target="_blank"
          rel="noopener noreferrer"
        >
          <div className="polymarket-largest-left">
            <span className="polymarket-stat-label">{t('polymarket.largestTrade')}</span>
            <span className="polymarket-largest-value">{formatDollarAmount(summary.largestTrade || 0)}</span>
          </div>
          <div className="polymarket-largest-divider"></div>
          <div className="polymarket-largest-right">
            <span className="polymarket-largest-market-label">{t('polymarket.marketInfo')}</span>
            <div className="polymarket-largest-market-row">
              {largestTrade.marketIcon && (
                <img
                  className="polymarket-largest-market-icon"
                  src={largestTrade.marketIcon}
                  alt=""
                />
              )}
              <div className="polymarket-largest-market-details">
                <span className="polymarket-largest-market-title">
                  {largestTrade.marketTitle || largestTrade.marketSlug}
                </span>
                <span className={`polymarket-largest-side ${isYes ? 'yes' : 'no'}`}>
                  {displaySide}
                </span>
              </div>
            </div>
          </div>
          <svg className="polymarket-largest-arrow" viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
            <path d="M13.172 12l-4.95-4.95 1.414-1.414L16 12l-6.364 6.364-1.414-1.414z"/>
          </svg>
        </a>
      ) : (
        <div className="polymarket-largest-block empty">
          <div className="polymarket-largest-left">
            <span className="polymarket-stat-label">{t('polymarket.largestTrade')}</span>
            <span className="polymarket-largest-value">-</span>
          </div>
          <div className="polymarket-largest-divider"></div>
          <div className="polymarket-largest-right">
            <span className="polymarket-largest-market-label">{t('polymarket.marketInfo')}</span>
            <span className="polymarket-largest-empty">{t('polymarket.noTrades')}</span>
          </div>
        </div>
      )}
    </div>
  );
}

export default PolymarketStats;
